import { Injectable, inject } from '@angular/core';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { WebViewBridgeService } from '../webview/webview-bridge.service';
import { WebViewAction } from '../../enums/webview-action';
import { PosthogService } from './posthog.service';

export type CheckoutPlan = 'MONTHLY' | 'YEARLY';

/**
 * Ce qu'un tunnel d'achat rend en revenant.
 *
 * Les deux canaux ne racontent pas la même histoire, et les confondre coûterait
 * cher : Stripe s'ouvre ailleurs et on ne sait rien de la suite, tandis que
 * l'achat intégré se conclut sur place et sait dire s'il a abouti.
 *
 * `cancelled` compte autant que le reste : chez Apple, refermer la feuille
 * d'achat est un geste ordinaire. Le traiter comme une erreur afficherait un
 * message rouge à quelqu'un qui a simplement changé d'avis.
 */
export type CheckoutOutcome =
  /** Le tunnel est ouvert ailleurs ; l'issue viendra par notre webhook. */
  | 'opened'
  /** L'achat a abouti. */
  | 'completed'
  /** L'utilisateur a renoncé. Ni erreur, ni message. */
  | 'cancelled'
  /** En attente d'un accord extérieur (« Demander à acheter »). */
  | 'pending';

/**
 * Prix d'une formule, tel que le fournisseur le FACTURE.
 *
 * Montant numérique et devise séparés, jamais une chaîne déjà formatée : la mise
 * en forme dépend de la locale de l'appareil, et le calcul de l'économie
 * annuelle a besoin des nombres.
 */
export interface PlanPrice {
  plan: CheckoutPlan;
  /** Unité majeure : 3.99, pas 399. */
  amount: number;
  /** Code ISO 4217 en majuscules. */
  currency: string;
  /**
   * Montant déjà mis en forme par le MAGASIN, quand il en fournit un.
   *
   * Absent côté Stripe, où c'est le front qui met en forme. Présent côté achat
   * intégré, et alors prioritaire : le magasin connaît les conventions du pays
   * du compte mieux que nous, et c'est lui qui facture.
   */
  priceString?: string;
}

/**
 * Contrat d'un moyen de paiement.
 *
 * Cette interface existe pour une raison précise : si Apple ou Google exigent
 * un jour l'achat in-app, on écrit un `IapCheckoutProvider` qui l'implémente et
 * AUCUN écran ne change. Le bouton d'achat, lui, vit dans l'app web : on peut
 * donc basculer sans passer par une release des stores.
 */
export interface CheckoutProvider {
  /** Ouvre le tunnel d'achat et dit ce qu'il en est advenu. */
  purchase(plan: CheckoutPlan): Promise<CheckoutOutcome>;
  /** Factures et moyen de paiement. */
  manageBilling(): Promise<void>;
  /** Ce moyen de paiement est-il utilisable dans le contexte courant ? */
  isAvailable(): boolean;

  /**
   * Prix des formules, tels que CE fournisseur les facture.
   *
   * C'est le point qui rendra l'achat intégré correct : sur mobile, les montants
   * viendront du magasin (localisés, dans la devise du pays), pas de nos
   * libellés. Aucun écran ne changera — l'interface était faite pour ça.
   */
  getPrices(): Promise<PlanPrice[]>;
}

/**
 * Paiement par Stripe, ouvert dans le NAVIGATEUR EXTERNE.
 *
 * ⚠️ ATTENTION — ce n'est PAS une voie conforme, contrairement à ce que ce
 * commentaire affirmait. Le « modèle Spotify » repose sur l'exception des
 * *reader apps* (règle 3.1.3(a) : presse, musique, vidéo, livres), à laquelle
 * Vegiwise ne peut pas prétendre. Pour une app ordinaire, un bouton qui mène à
 * un paiement extérieur est de l'ORIENTATION, que la règle 3.1.3 interdit —
 * sauf ouvertures récentes propres aux États-Unis et à l'Europe, qui
 * demandent des autorisations spécifiques.
 *
 * Sortir la transaction de la WebView évite le pire (une transaction tierce
 * exécutée DANS l'app), mais ne met pas à l'abri d'un refus ni d'un retrait.
 * La sortie durable est l'achat intégré : `CheckoutProvider` existe pour qu'un
 * `IapCheckoutProvider` prenne cette place sans qu'aucun écran ne change.
 *
 * Voir `analyses/product/achat-integre/`.
 */
@Injectable({ providedIn: 'root' })
export class CheckoutService implements CheckoutProvider {
  private readonly subscriptionApi = new SubscriptionApi();
  private readonly webViewBridge = inject(WebViewBridgeService);
  private readonly posthog = inject(PosthogService);

  /** Un achat a été lancé : sert à resynchroniser au retour dans l'app. */
  private purchaseInFlight = false;

  isAvailable(): boolean {
    return true;
  }

  /** Montants réellement facturés par Stripe, résolus côté serveur. */
  async getPrices(): Promise<PlanPrice[]> {
    const plans = await this.subscriptionApi.getPlans();
    return plans.map((plan) => ({
      plan: plan.plan as CheckoutPlan,
      amount: plan.amount,
      currency: plan.currency,
    }));
  }

  async purchase(plan: CheckoutPlan): Promise<CheckoutOutcome> {
    // Les navigateurs mobiles n'autorisent `window.open` que pendant la partie
    // SYNCHRONE du geste utilisateur : on pré-ouvre l'onglet AVANT tout await,
    // et on y pose l'URL ensuite. Sans cela, le bloqueur de fenêtres surgissantes
    // avale le tunnel de paiement.
    const pendingTab = this.hasNativeChannel()
      ? null
      : window.open('', '_blank');

    try {
      const { url } = await this.subscriptionApi.createCheckoutSession(plan);
      this.purchaseInFlight = true;
      this.posthog.capture('checkout_started', { plan });
      this.openExternal(url, pendingTab);
      // `opened` et non `completed` : le paiement se conclut hors de l'app, et
      // c'est notre webhook qui nous l'apprendra.
      return 'opened';
    } catch (error) {
      pendingTab?.close();
      throw error;
    }
  }

  async manageBilling(): Promise<void> {
    const pendingTab = this.hasNativeChannel()
      ? null
      : window.open('', '_blank');
    try {
      const { url } = await this.subscriptionApi.createPortalSession();
      this.openExternal(url, pendingTab);
    } catch (error) {
      pendingTab?.close();
      throw error;
    }
  }

  /** Un achat vient-il d'être lancé ? (consommé une seule fois) */
  consumePurchaseInFlight(): boolean {
    const value = this.purchaseInFlight;
    this.purchaseInFlight = false;
    return value;
  }

  private hasNativeChannel(): boolean {
    return (
      this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl) ||
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    );
  }

  /**
   * Ouvre dans le navigateur EXTERNE. Cascade identique à celle du renvoi vers
   * la boutique : canal dédié → canal OAuth (indispensable, le build actuel des
   * stores ne connaît pas encore `openExternalUrl`) → onglet pré-ouvert → dernier
   * recours.
   */
  private openExternal(url: string, pendingTab: Window | null): void {
    if (this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl)) {
      this.webViewBridge.postMessageToChannel(
        WebViewAction.openExternalUrl,
        url,
      );
    } else if (
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    ) {
      this.webViewBridge.postMessageToChannel(WebViewAction.openOAuthUrl, url);
    } else if (pendingTab && !pendingTab.closed) {
      pendingTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
